import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { 
  ChevronLeft, 
  ChevronRight, 
  SlidersHorizontal,
  Mountain,
  Waves,
  Wheat,
  CloudSun,
  MountainSnow,
  Palmtree,
  Tractor,
  Home,
  Trees,
  Castle,
  X
} from 'lucide-react';
import { categories } from '../data/mockData';

const iconMap = {
  Mountain,
  Waves,
  Wheat,
  CloudSun,
  MountainSnow,
  Palmtree,
  Tractor,
  Home,
  Trees,
  Castle
};

const CategoryFilter = ({ selectedCategory, onCategoryChange, onFiltersChange }) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [minGuests, setMinGuests] = useState(1);
  const [bedrooms, setBedrooms] = useState(0);
  const scrollContainerRef = useRef(null);

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 400;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleApplyFilters = () => {
    if (onFiltersChange) {
      onFiltersChange({
        minPrice: priceRange[0],
        maxPrice: priceRange[1],
        guests: minGuests > 1 ? minGuests : null,
        bedrooms: bedrooms > 0 ? bedrooms : null
      });
    }
    setShowFiltersModal(false);
  };

  const handleClearFilters = () => {
    setPriceRange([0, 50000]);
    setMinGuests(1);
    setBedrooms(0);
    if (onFiltersChange) {
      onFiltersChange({
        minPrice: null,
        maxPrice: null,
        guests: null,
        bedrooms: null
      });
    }
    setShowFiltersModal(false);
  };

  return (
    <div className="sticky top-20 z-40 bg-white border-b shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center py-5 gap-4">
          {/* Categories with scroll */}
          <div className="relative flex-1 min-w-0">
            {/* Left Arrow */}
            {showLeftArrow && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-110 transition-all"
                onClick={() => scroll('left')}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}

            {/* Categories */}
            <div
              ref={scrollContainerRef}
              className="flex space-x-4 overflow-x-auto scrollbar-hide px-12"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((category) => {
                const IconComponent = iconMap[category.iconName];
                const isActive = selectedCategory === category.name;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => onCategoryChange(isActive ? null : category.name)}
                    className={`flex flex-col items-center space-y-2 min-w-fit px-4 py-3 rounded-xl transition-all hover:bg-gray-50 group ${
                      isActive
                        ? 'bg-green-50 border-2 border-[#7CB342] shadow-md'
                        : 'border-2 border-transparent hover:border-gray-200'
                    }`}
                  >
                    <div className={`p-2 rounded-full transition-all ${
                      isActive 
                        ? 'bg-[#7CB342] text-white scale-110' 
                        : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:scale-105'
                    }`}>
                      {IconComponent && <IconComponent className="h-6 w-6" />}
                    </div>
                    <span className={`text-xs font-semibold whitespace-nowrap transition-colors ${
                      isActive ? 'text-[#7CB342]' : 'text-gray-700 group-hover:text-gray-900'
                    }`}>
                      {category.name}
                    </span>
                    {isActive && (
                      <div className="h-0.5 w-full bg-[#7CB342] rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right Arrow */}
            {showRightArrow && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-110 transition-all"
                onClick={() => scroll('right')}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            )}
          </div>

          {/* Filters Button - outside the scroll container */}
          <Button
            variant="outline"
            className="flex-shrink-0 rounded-xl border-2 border-gray-300 hover:border-[#7CB342] hover:bg-green-50 transition-all whitespace-nowrap shadow-md px-4 py-2"
            onClick={() => setShowFiltersModal(true)}
            data-testid="filters-button"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Filters</span>
          </Button>
        </div>
      </div>

      {/* Filters Modal */}
      <Dialog open={showFiltersModal} onOpenChange={setShowFiltersModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Filters</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Price Range */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Price Range (per night)</Label>
              <div className="px-2">
                <Slider
                  value={priceRange}
                  onValueChange={(values) => {
                    // Ensure min is not negative and max >= min
                    const newMin = Math.max(0, values[0]);
                    const newMax = Math.max(values[1], newMin);
                    setPriceRange([newMin, newMax]);
                  }}
                  min={0}
                  max={50000}
                  step={500}
                  className="w-full"
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500">Min Price</Label>
                  <div className="flex items-center mt-1">
                    <span className="text-gray-500 mr-1">₹</span>
                    <Input
                      type="number"
                      min={0}
                      value={priceRange[0]}
                      onChange={(e) => {
                        const newMin = Math.max(0, parseInt(e.target.value) || 0);
                        // Ensure min doesn't exceed max
                        setPriceRange([Math.min(newMin, priceRange[1]), priceRange[1]]);
                      }}
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-gray-500">Max Price</Label>
                  <div className="flex items-center mt-1">
                    <span className="text-gray-500 mr-1">₹</span>
                    <Input
                      type="number"
                      min={priceRange[0]}
                      value={priceRange[1]}
                      onChange={(e) => {
                        const newMax = parseInt(e.target.value) || 50000;
                        // Ensure max is not less than min
                        setPriceRange([priceRange[0], Math.max(newMax, priceRange[0])]);
                      }}
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Guests */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Minimum Guests</Label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 rounded-full"
                  onClick={() => setMinGuests(Math.max(1, minGuests - 1))}
                  disabled={minGuests <= 1}
                >
                  <span className="text-lg">-</span>
                </Button>
                <span className="text-xl font-semibold w-8 text-center">{minGuests}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 rounded-full"
                  onClick={() => setMinGuests(minGuests + 1)}
                >
                  <span className="text-lg">+</span>
                </Button>
              </div>
            </div>

            {/* Bedrooms */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Minimum Bedrooms</Label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 rounded-full"
                  onClick={() => setBedrooms(Math.max(0, bedrooms - 1))}
                  disabled={bedrooms <= 0}
                >
                  <span className="text-lg">-</span>
                </Button>
                <span className="text-xl font-semibold w-8 text-center">{bedrooms === 0 ? 'Any' : bedrooms}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 rounded-full"
                  onClick={() => setBedrooms(bedrooms + 1)}
                >
                  <span className="text-lg">+</span>
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button
              variant="ghost"
              onClick={handleClearFilters}
              className="flex-1"
            >
              Clear all
            </Button>
            <Button
              onClick={handleApplyFilters}
              className="flex-1 bg-[#7CB342] hover:bg-[#6da036] text-white"
            >
              Show results
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryFilter;